/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  AcceptInviteBody,
  AdminClearUserPermissionBody,
  AdminDeleteRoleParams,
  AdminInviteStaff201,
  AdminInviteStaffBody,
  AdminListPermissions200,
  AdminListRoles200,
  AdminListUsersParams,
  AdminSetUserBannedBody,
  AdminSetUserPermissionBody,
  AdminSetUserRoleBody,
  AdminUserDetail,
  AdminUserPage,
  ChangePasswordBody,
  ConfirmTotpEnrolment200,
  ConfirmTotpEnrolmentBody,
  ConflictResponse,
  EmailPreferences,
  ForbiddenResponse,
  ForgotPasswordBody,
  InternalErrorResponse,
  ListSessions200,
  LoginRequest,
  LoginWithGoogleBody,
  Me,
  NotFoundResponse,
  PayloadTooLargeResponse,
  Register201,
  RegisterRequest,
  ResetPasswordBody,
  Role,
  RoleInput,
  Session,
  StepUpBody,
  StepUpToken,
  TooManyRequestsResponse,
  TotpChallenge,
  TotpEnrolment,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse,
  UnsubscribeEmail200,
  UnsubscribeEmailBody,
  UnsubscribeEmailParams,
  UpdateProfileBody,
  User,
  VerifyEmailBody,
  VerifyTotpBody
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getLoginUrl = () => {




  return `/auth/login`
}

/**
 * Answers a session, or a TOTP challenge when the account has TOTP on;
 * the challenge is then answered at `POST /auth/totp/verify`. Five
 * wrong passwords lock the account for 15 minutes. Production also
 * checks a Cloudflare Turnstile token.
 * @summary Sign in with an email and password
 */
export const login = async (loginRequest: LoginRequest, options?: Parameters<typeof apiFetch>[1]): Promise<Session | TotpChallenge> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Session | TotpChallenge>(getLoginUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(loginRequest)
  }
);}





export const getLoginMutationKey = () => ['login'] as const;

export const getLoginMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof login>>, TError,LoginMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof login>>, TError,LoginMutationVariables, TContext> => {

const mutationKey = getLoginMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof login>>, LoginMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  login(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LoginMutationResult = NonNullable<Awaited<ReturnType<typeof login>>>
    export type LoginMutationBody = LoginRequest
    export type LoginMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type LoginMutationVariables = {data: LoginRequest}

    /**
 * @summary Sign in with an email and password
 */
export const useLogin = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof login>>, TError,LoginMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof login>>,
        TError,
        LoginMutationVariables,
        TContext
      > => {
      return useMutation(getLoginMutationOptions(options), queryClient);
    }
    export const getLoginWithGoogleUrl = () => {




  return `/auth/google`
}

/**
 * Refused with 403 `standalone_disabled` in standalone mode.
 * @summary Sign in or sign up with a Google ID token
 */
export const loginWithGoogle = async (loginWithGoogleBody: LoginWithGoogleBody, options?: Parameters<typeof apiFetch>[1]): Promise<Session | TotpChallenge> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Session | TotpChallenge>(getLoginWithGoogleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(loginWithGoogleBody)
  }
);}





export const getLoginWithGoogleMutationKey = () => ['loginWithGoogle'] as const;

export const getLoginWithGoogleMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof loginWithGoogle>>, TError,LoginWithGoogleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof loginWithGoogle>>, TError,LoginWithGoogleMutationVariables, TContext> => {

const mutationKey = getLoginWithGoogleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof loginWithGoogle>>, LoginWithGoogleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  loginWithGoogle(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LoginWithGoogleMutationResult = NonNullable<Awaited<ReturnType<typeof loginWithGoogle>>>
    export type LoginWithGoogleMutationBody = LoginWithGoogleBody
    export type LoginWithGoogleMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type LoginWithGoogleMutationVariables = {data: LoginWithGoogleBody}

    /**
 * @summary Sign in or sign up with a Google ID token
 */
export const useLoginWithGoogle = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof loginWithGoogle>>, TError,LoginWithGoogleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof loginWithGoogle>>,
        TError,
        LoginWithGoogleMutationVariables,
        TContext
      > => {
      return useMutation(getLoginWithGoogleMutationOptions(options), queryClient);
    }
    export const getVerifyTotpUrl = () => {




  return `/auth/totp/verify`
}

/**
 * The code is a 6-digit TOTP or a recovery code. A challenge survives five wrong codes.
 * @summary Answer a sign-in's TOTP challenge
 */
export const verifyTotp = async (verifyTotpBody: VerifyTotpBody, options?: Parameters<typeof apiFetch>[1]): Promise<Session> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Session>(getVerifyTotpUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(verifyTotpBody)
  }
);}





export const getVerifyTotpMutationKey = () => ['verifyTotp'] as const;

export const getVerifyTotpMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyTotp>>, TError,VerifyTotpMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof verifyTotp>>, TError,VerifyTotpMutationVariables, TContext> => {

const mutationKey = getVerifyTotpMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof verifyTotp>>, VerifyTotpMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  verifyTotp(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyTotpMutationResult = NonNullable<Awaited<ReturnType<typeof verifyTotp>>>
    export type VerifyTotpMutationBody = VerifyTotpBody
    export type VerifyTotpMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type VerifyTotpMutationVariables = {data: VerifyTotpBody}

    /**
 * @summary Answer a sign-in's TOTP challenge
 */
export const useVerifyTotp = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyTotp>>, TError,VerifyTotpMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof verifyTotp>>,
        TError,
        VerifyTotpMutationVariables,
        TContext
      > => {
      return useMutation(getVerifyTotpMutationOptions(options), queryClient);
    }
    export const getRefreshSessionUrl = () => {




  return `/auth/refresh`
}

/**
 * Reads the `devsms_refresh` cookie (HttpOnly, SameSite=Strict, path
 * `/api/v2/auth`) and answers a new session with a rotated cookie. A
 * refused refresh also deletes the cookie; replaying a rotated token
 * revokes its whole session family (401 `refresh_reused`).
 * @summary Rotate the refresh cookie for a new access token
 */
export const refreshSession = async ( options?: Parameters<typeof apiFetch>[1]): Promise<Session> => {

  return apiFetch<Session>(getRefreshSessionUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRefreshSessionMutationKey = () => ['refreshSession'] as const;

export const getRefreshSessionMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshSession>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshSession>>, TError,void, TContext> => {

const mutationKey = getRefreshSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshSession>>, void> = () => {


          return  refreshSession(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshSessionMutationResult = NonNullable<Awaited<ReturnType<typeof refreshSession>>>

    export type RefreshSessionMutationError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


    /**
 * @summary Rotate the refresh cookie for a new access token
 */
export const useRefreshSession = <TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshSession>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof refreshSession>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRefreshSessionMutationOptions(options), queryClient);
    }
    export const getLogoutUrl = () => {




  return `/auth/logout`
}

/**
 * Deletes the refresh cookie. Without a cookie it still answers 204.
 * @summary End the session the refresh cookie names
 */
export const logout = async ( options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getLogoutUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getLogoutMutationKey = () => ['logout'] as const;

export const getLogoutMutationOptions = <TError = InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError,void, TContext> => {

const mutationKey = getLogoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof logout>>, void> = () => {


          return  logout(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LogoutMutationResult = NonNullable<Awaited<ReturnType<typeof logout>>>

    export type LogoutMutationError = InternalErrorResponse | UnavailableResponse


    /**
 * @summary End the session the refresh cookie names
 */
export const useLogout = <TError = InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof logout>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getLogoutMutationOptions(options), queryClient);
    }
    export const getRegisterUrl = () => {




  return `/auth/register`
}

/**
 * Creates a customer account and mails a verification link. Refused
 * with 403 `standalone_disabled` in standalone mode.
 * @summary Sign up with an email and password
 */
export const register = async (registerRequest: RegisterRequest, options?: Parameters<typeof apiFetch>[1]): Promise<Register201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Register201>(getRegisterUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(registerRequest)
  }
);}





export const getRegisterMutationKey = () => ['register'] as const;

export const getRegisterMutationOptions = <TError = ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof register>>, TError,RegisterMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof register>>, TError,RegisterMutationVariables, TContext> => {

const mutationKey = getRegisterMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof register>>, RegisterMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  register(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegisterMutationResult = NonNullable<Awaited<ReturnType<typeof register>>>
    export type RegisterMutationBody = RegisterRequest
    export type RegisterMutationError = ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type RegisterMutationVariables = {data: RegisterRequest}

    /**
 * @summary Sign up with an email and password
 */
export const useRegister = <TError = ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof register>>, TError,RegisterMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof register>>,
        TError,
        RegisterMutationVariables,
        TContext
      > => {
      return useMutation(getRegisterMutationOptions(options), queryClient);
    }
    export const getVerifyEmailUrl = () => {




  return `/auth/verify-email`
}

/**
 * @summary Confirm an email address with the emailed token
 */
export const verifyEmail = async (verifyEmailBody: VerifyEmailBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getVerifyEmailUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(verifyEmailBody)
  }
);}





export const getVerifyEmailMutationKey = () => ['verifyEmail'] as const;

export const getVerifyEmailMutationOptions = <TError = PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyEmail>>, TError,VerifyEmailMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof verifyEmail>>, TError,VerifyEmailMutationVariables, TContext> => {

const mutationKey = getVerifyEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof verifyEmail>>, VerifyEmailMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  verifyEmail(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyEmailMutationResult = NonNullable<Awaited<ReturnType<typeof verifyEmail>>>
    export type VerifyEmailMutationBody = VerifyEmailBody
    export type VerifyEmailMutationError = PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type VerifyEmailMutationVariables = {data: VerifyEmailBody}

    /**
 * @summary Confirm an email address with the emailed token
 */
export const useVerifyEmail = <TError = PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyEmail>>, TError,VerifyEmailMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof verifyEmail>>,
        TError,
        VerifyEmailMutationVariables,
        TContext
      > => {
      return useMutation(getVerifyEmailMutationOptions(options), queryClient);
    }
    export const getResendVerificationEmailUrl = () => {




  return `/auth/verify-email/resend`
}

/**
 * At most five verification emails per 24 hours (429 `too_many_emails`).
 * @summary Mail the caller a new verification link
 */
export const resendVerificationEmail = async ( options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getResendVerificationEmailUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getResendVerificationEmailMutationKey = () => ['resendVerificationEmail'] as const;

export const getResendVerificationEmailMutationOptions = <TError = UnauthorizedResponse | ConflictResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resendVerificationEmail>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resendVerificationEmail>>, TError,void, TContext> => {

const mutationKey = getResendVerificationEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resendVerificationEmail>>, void> = () => {


          return  resendVerificationEmail(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResendVerificationEmailMutationResult = NonNullable<Awaited<ReturnType<typeof resendVerificationEmail>>>

    export type ResendVerificationEmailMutationError = UnauthorizedResponse | ConflictResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse


    /**
 * @summary Mail the caller a new verification link
 */
export const useResendVerificationEmail = <TError = UnauthorizedResponse | ConflictResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resendVerificationEmail>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof resendVerificationEmail>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getResendVerificationEmailMutationOptions(options), queryClient);
    }
    export const getForgotPasswordUrl = () => {




  return `/auth/forgot-password`
}

/**
 * Answers 202 whether or not the address has an account (or is given at all), so it reveals none.
 * @summary Mail a password reset code
 */
export const forgotPassword = async (forgotPasswordBody: ForgotPasswordBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getForgotPasswordUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(forgotPasswordBody)
  }
);}





export const getForgotPasswordMutationKey = () => ['forgotPassword'] as const;

export const getForgotPasswordMutationOptions = <TError = PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof forgotPassword>>, TError,ForgotPasswordMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof forgotPassword>>, TError,ForgotPasswordMutationVariables, TContext> => {

const mutationKey = getForgotPasswordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof forgotPassword>>, ForgotPasswordMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  forgotPassword(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ForgotPasswordMutationResult = NonNullable<Awaited<ReturnType<typeof forgotPassword>>>
    export type ForgotPasswordMutationBody = ForgotPasswordBody
    export type ForgotPasswordMutationError = PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type ForgotPasswordMutationVariables = {data: ForgotPasswordBody}

    /**
 * @summary Mail a password reset code
 */
export const useForgotPassword = <TError = PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof forgotPassword>>, TError,ForgotPasswordMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof forgotPassword>>,
        TError,
        ForgotPasswordMutationVariables,
        TContext
      > => {
      return useMutation(getForgotPasswordMutationOptions(options), queryClient);
    }
    export const getResetPasswordUrl = () => {




  return `/auth/reset-password`
}

/**
 * Ends every session of the account.
 * @summary Set a new password with the emailed code
 */
export const resetPassword = async (resetPasswordBody: ResetPasswordBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getResetPasswordUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(resetPasswordBody)
  }
);}





export const getResetPasswordMutationKey = () => ['resetPassword'] as const;

export const getResetPasswordMutationOptions = <TError = PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetPassword>>, TError,ResetPasswordMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resetPassword>>, TError,ResetPasswordMutationVariables, TContext> => {

const mutationKey = getResetPasswordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resetPassword>>, ResetPasswordMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  resetPassword(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetPasswordMutationResult = NonNullable<Awaited<ReturnType<typeof resetPassword>>>
    export type ResetPasswordMutationBody = ResetPasswordBody
    export type ResetPasswordMutationError = PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type ResetPasswordMutationVariables = {data: ResetPasswordBody}

    /**
 * @summary Set a new password with the emailed code
 */
export const useResetPassword = <TError = PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetPassword>>, TError,ResetPasswordMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof resetPassword>>,
        TError,
        ResetPasswordMutationVariables,
        TContext
      > => {
      return useMutation(getResetPasswordMutationOptions(options), queryClient);
    }
    export const getUnsubscribeEmailUrl = (params: UnsubscribeEmailParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/auth/unsubscribe?${stringifiedParams}` : `/auth/unsubscribe`
}

/**
 * The one-click unsubscribe of RFC 8058, the URL an optional email's List-Unsubscribe header names: the signed token names the account and the preference, so no sign-in is needed. A mail client posts `List-Unsubscribe=One-Click`; the body is not read. Repeating it is harmless. An altered or unknown token is 422 `token_invalid`.
 * @summary Turn off one optional email from its unsubscribe link
 */
export const unsubscribeEmail = async (params: UnsubscribeEmailParams,
    unsubscribeEmailBody?: UnsubscribeEmailBody, options?: Parameters<typeof apiFetch>[1]): Promise<UnsubscribeEmail200> => {
    const formUrlEncoded = new URLSearchParams();
if(unsubscribeEmailBody?.['List-Unsubscribe'] !== undefined) {
 formUrlEncoded.append(`List-Unsubscribe`, unsubscribeEmailBody['List-Unsubscribe']);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<UnsubscribeEmail200>(getUnsubscribeEmailUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getUnsubscribeEmailMutationKey = () => ['unsubscribeEmail'] as const;

export const getUnsubscribeEmailMutationOptions = <TError = PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unsubscribeEmail>>, TError,UnsubscribeEmailMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unsubscribeEmail>>, TError,UnsubscribeEmailMutationVariables, TContext> => {

const mutationKey = getUnsubscribeEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unsubscribeEmail>>, UnsubscribeEmailMutationVariables> = (props) => {
          const {params,data} = props ?? {};

          return  unsubscribeEmail(params,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnsubscribeEmailMutationResult = NonNullable<Awaited<ReturnType<typeof unsubscribeEmail>>>
    export type UnsubscribeEmailMutationBody = UnsubscribeEmailBody | undefined
    export type UnsubscribeEmailMutationError = PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type UnsubscribeEmailMutationVariables = {params: UnsubscribeEmailParams;data?: UnsubscribeEmailBody}

    /**
 * @summary Turn off one optional email from its unsubscribe link
 */
export const useUnsubscribeEmail = <TError = PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unsubscribeEmail>>, TError,UnsubscribeEmailMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unsubscribeEmail>>,
        TError,
        UnsubscribeEmailMutationVariables,
        TContext
      > => {
      return useMutation(getUnsubscribeEmailMutationOptions(options), queryClient);
    }
    export const getAcceptInviteUrl = () => {




  return `/auth/accept-invite`
}

/**
 * @summary Accept a staff invitation and sign in
 */
export const acceptInvite = async (acceptInviteBody: AcceptInviteBody, options?: Parameters<typeof apiFetch>[1]): Promise<Session> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Session>(getAcceptInviteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(acceptInviteBody)
  }
);}





export const getAcceptInviteMutationKey = () => ['acceptInvite'] as const;

export const getAcceptInviteMutationOptions = <TError = ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptInvite>>, TError,AcceptInviteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptInvite>>, TError,AcceptInviteMutationVariables, TContext> => {

const mutationKey = getAcceptInviteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptInvite>>, AcceptInviteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  acceptInvite(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptInviteMutationResult = NonNullable<Awaited<ReturnType<typeof acceptInvite>>>
    export type AcceptInviteMutationBody = AcceptInviteBody
    export type AcceptInviteMutationError = ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AcceptInviteMutationVariables = {data: AcceptInviteBody}

    /**
 * @summary Accept a staff invitation and sign in
 */
export const useAcceptInvite = <TError = ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptInvite>>, TError,AcceptInviteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof acceptInvite>>,
        TError,
        AcceptInviteMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptInviteMutationOptions(options), queryClient);
    }
    export const getStepUpUrl = () => {




  return `/auth/step-up`
}

/**
 * The token is bound to the caller's session and lasts 5 minutes.
 * With TOTP on, `code` is required too.
 * @summary Prove the password again for an X-Step-Up token
 */
export const stepUp = async (stepUpBody: StepUpBody, options?: Parameters<typeof apiFetch>[1]): Promise<StepUpToken> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<StepUpToken>(getStepUpUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(stepUpBody)
  }
);}





export const getStepUpMutationKey = () => ['stepUp'] as const;

export const getStepUpMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stepUp>>, TError,StepUpMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof stepUp>>, TError,StepUpMutationVariables, TContext> => {

const mutationKey = getStepUpMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof stepUp>>, StepUpMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  stepUp(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StepUpMutationResult = NonNullable<Awaited<ReturnType<typeof stepUp>>>
    export type StepUpMutationBody = StepUpBody
    export type StepUpMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type StepUpMutationVariables = {data: StepUpBody}

    /**
 * @summary Prove the password again for an X-Step-Up token
 */
export const useStepUp = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stepUp>>, TError,StepUpMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof stepUp>>,
        TError,
        StepUpMutationVariables,
        TContext
      > => {
      return useMutation(getStepUpMutationOptions(options), queryClient);
    }
    export const getGetMeUrl = () => {




  return `/auth/me`
}

/**
 * @summary The caller, their role, permissions and the deployment mode
 */
export const getMe = async ( options?: Parameters<typeof apiFetch>[1]): Promise<Me> => {

  return apiFetch<Me>(getGetMeUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMeQueryKey = () => {
    return [
    `/auth/me`
    ] as const;
    }


export const getGetMeQueryOptions = <TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMeQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMe>>> = ({ signal }) => getMe({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMeQueryResult = NonNullable<Awaited<ReturnType<typeof getMe>>>
export type GetMeQueryError = UnauthorizedResponse | InternalErrorResponse | UnavailableResponse


export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMe>>,
          TError,
          Awaited<ReturnType<typeof getMe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMe>>,
          TError,
          Awaited<ReturnType<typeof getMe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller, their role, permissions and the deployment mode
 */

export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMeQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getBeginTotpEnrolmentUrl = () => {




  return `/account/totp/begin`
}

/**
 * Nothing changes until `POST /account/totp/confirm` proves the secret.
 * @summary Draw a TOTP secret to enrol
 */
export const beginTotpEnrolment = async ( options?: Parameters<typeof apiFetch>[1]): Promise<TotpEnrolment> => {

  return apiFetch<TotpEnrolment>(getBeginTotpEnrolmentUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getBeginTotpEnrolmentMutationKey = () => ['beginTotpEnrolment'] as const;

export const getBeginTotpEnrolmentMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof beginTotpEnrolment>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof beginTotpEnrolment>>, TError,void, TContext> => {

const mutationKey = getBeginTotpEnrolmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof beginTotpEnrolment>>, void> = () => {


          return  beginTotpEnrolment(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BeginTotpEnrolmentMutationResult = NonNullable<Awaited<ReturnType<typeof beginTotpEnrolment>>>

    export type BeginTotpEnrolmentMutationError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse


    /**
 * @summary Draw a TOTP secret to enrol
 */
export const useBeginTotpEnrolment = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof beginTotpEnrolment>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof beginTotpEnrolment>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getBeginTotpEnrolmentMutationOptions(options), queryClient);
    }
    export const getConfirmTotpEnrolmentUrl = () => {




  return `/account/totp/confirm`
}

/**
 * @summary Prove the drawn secret and turn TOTP on
 */
export const confirmTotpEnrolment = async (confirmTotpEnrolmentBody: ConfirmTotpEnrolmentBody, options?: Parameters<typeof apiFetch>[1]): Promise<ConfirmTotpEnrolment200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<ConfirmTotpEnrolment200>(getConfirmTotpEnrolmentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmTotpEnrolmentBody)
  }
);}





export const getConfirmTotpEnrolmentMutationKey = () => ['confirmTotpEnrolment'] as const;

export const getConfirmTotpEnrolmentMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTotpEnrolment>>, TError,ConfirmTotpEnrolmentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmTotpEnrolment>>, TError,ConfirmTotpEnrolmentMutationVariables, TContext> => {

const mutationKey = getConfirmTotpEnrolmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmTotpEnrolment>>, ConfirmTotpEnrolmentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  confirmTotpEnrolment(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmTotpEnrolmentMutationResult = NonNullable<Awaited<ReturnType<typeof confirmTotpEnrolment>>>
    export type ConfirmTotpEnrolmentMutationBody = ConfirmTotpEnrolmentBody
    export type ConfirmTotpEnrolmentMutationError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type ConfirmTotpEnrolmentMutationVariables = {data: ConfirmTotpEnrolmentBody}

    /**
 * @summary Prove the drawn secret and turn TOTP on
 */
export const useConfirmTotpEnrolment = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTotpEnrolment>>, TError,ConfirmTotpEnrolmentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof confirmTotpEnrolment>>,
        TError,
        ConfirmTotpEnrolmentMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmTotpEnrolmentMutationOptions(options), queryClient);
    }
    export const getDisableTotpUrl = () => {




  return `/account/totp/disable`
}

/**
 * Refused for staff, for whom TOTP is mandatory (403 `totp_required_for_staff`).
 * @summary Turn TOTP off
 */
export const disableTotp = async ( options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDisableTotpUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDisableTotpMutationKey = () => ['disableTotp'] as const;

export const getDisableTotpMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disableTotp>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disableTotp>>, TError,void, TContext> => {

const mutationKey = getDisableTotpMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disableTotp>>, void> = () => {


          return  disableTotp(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisableTotpMutationResult = NonNullable<Awaited<ReturnType<typeof disableTotp>>>

    export type DisableTotpMutationError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


    /**
 * @summary Turn TOTP off
 */
export const useDisableTotp = <TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disableTotp>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof disableTotp>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDisableTotpMutationOptions(options), queryClient);
    }
    export const getListSessionsUrl = () => {




  return `/account/sessions`
}

/**
 * @summary The caller's live sign-ins
 */
export const listSessions = async ( options?: Parameters<typeof apiFetch>[1]): Promise<ListSessions200> => {

  return apiFetch<ListSessions200>(getListSessionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSessionsQueryKey = () => {
    return [
    `/account/sessions`
    ] as const;
    }


export const getListSessionsQueryOptions = <TData = Awaited<ReturnType<typeof listSessions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSessions>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSessionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSessions>>> = ({ signal }) => listSessions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSessions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listSessions>>>
export type ListSessionsQueryError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


export function useListSessions<TData = Awaited<ReturnType<typeof listSessions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSessions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSessions>>,
          TError,
          Awaited<ReturnType<typeof listSessions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSessions<TData = Awaited<ReturnType<typeof listSessions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSessions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSessions>>,
          TError,
          Awaited<ReturnType<typeof listSessions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSessions<TData = Awaited<ReturnType<typeof listSessions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSessions>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's live sign-ins
 */

export function useListSessions<TData = Awaited<ReturnType<typeof listSessions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSessions>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListSessionsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getRevokeSessionUrl = (id: string,) => {




  return `/account/sessions/${id}`
}

/**
 * Ending the caller's own session also deletes its refresh cookie.
 * @summary End one sign-in
 */
export const revokeSession = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getRevokeSessionUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRevokeSessionMutationKey = () => ['revokeSession'] as const;

export const getRevokeSessionMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeSession>>, TError,RevokeSessionMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeSession>>, TError,RevokeSessionMutationVariables, TContext> => {

const mutationKey = getRevokeSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeSession>>, RevokeSessionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  revokeSession(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeSessionMutationResult = NonNullable<Awaited<ReturnType<typeof revokeSession>>>

    export type RevokeSessionMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type RevokeSessionMutationVariables = {id: string}

    /**
 * @summary End one sign-in
 */
export const useRevokeSession = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeSession>>, TError,RevokeSessionMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof revokeSession>>,
        TError,
        RevokeSessionMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeSessionMutationOptions(options), queryClient);
    }
    export const getChangePasswordUrl = () => {




  return `/account/password`
}

/**
 * Ends every other session of the account.
 * @summary Change the password
 */
export const changePassword = async (changePasswordBody: ChangePasswordBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getChangePasswordUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(changePasswordBody)
  }
);}





export const getChangePasswordMutationKey = () => ['changePassword'] as const;

export const getChangePasswordMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changePassword>>, TError,ChangePasswordMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof changePassword>>, TError,ChangePasswordMutationVariables, TContext> => {

const mutationKey = getChangePasswordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof changePassword>>, ChangePasswordMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  changePassword(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangePasswordMutationResult = NonNullable<Awaited<ReturnType<typeof changePassword>>>
    export type ChangePasswordMutationBody = ChangePasswordBody
    export type ChangePasswordMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type ChangePasswordMutationVariables = {data: ChangePasswordBody}

    /**
 * @summary Change the password
 */
export const useChangePassword = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changePassword>>, TError,ChangePasswordMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof changePassword>>,
        TError,
        ChangePasswordMutationVariables,
        TContext
      > => {
      return useMutation(getChangePasswordMutationOptions(options), queryClient);
    }
    export const getGetProfileUrl = () => {




  return `/account/profile`
}

/**
 * @summary The caller's profile
 */
export const getProfile = async ( options?: Parameters<typeof apiFetch>[1]): Promise<User> => {

  return apiFetch<User>(getGetProfileUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetProfileQueryKey = () => {
    return [
    `/account/profile`
    ] as const;
    }


export const getGetProfileQueryOptions = <TData = Awaited<ReturnType<typeof getProfile>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProfileQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProfile>>> = ({ signal }) => getProfile({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getProfile>>>
export type GetProfileQueryError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProfile>>,
          TError,
          Awaited<ReturnType<typeof getProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProfile>>,
          TError,
          Awaited<ReturnType<typeof getProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's profile
 */

export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetProfileQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getUpdateProfileUrl = () => {




  return `/account/profile`
}

/**
 * A field left out is left unchanged.
 * @summary Change the name, phone or language
 */
export const updateProfile = async (updateProfileBody: UpdateProfileBody, options?: Parameters<typeof apiFetch>[1]): Promise<User> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<User>(getUpdateProfileUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateProfileBody)
  }
);}





export const getUpdateProfileMutationKey = () => ['updateProfile'] as const;

export const getUpdateProfileMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProfile>>, TError,UpdateProfileMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateProfile>>, TError,UpdateProfileMutationVariables, TContext> => {

const mutationKey = getUpdateProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateProfile>>, UpdateProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateProfile>>>
    export type UpdateProfileMutationBody = UpdateProfileBody
    export type UpdateProfileMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type UpdateProfileMutationVariables = {data: UpdateProfileBody}

    /**
 * @summary Change the name, phone or language
 */
export const useUpdateProfile = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProfile>>, TError,UpdateProfileMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateProfile>>,
        TError,
        UpdateProfileMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateProfileMutationOptions(options), queryClient);
    }
    export const getGetEmailPreferencesUrl = () => {




  return `/account/email-preferences`
}

/**
 * Usage alerts (a plan's sending limits) and credit warnings (credit low or expiring) can be turned off; every other email concerns security, money or a plan and always goes. Both are on until turned off (DV-156).
 * @summary The optional emails the caller receives
 */
export const getEmailPreferences = async ( options?: Parameters<typeof apiFetch>[1]): Promise<EmailPreferences> => {

  return apiFetch<EmailPreferences>(getGetEmailPreferencesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEmailPreferencesQueryKey = () => {
    return [
    `/account/email-preferences`
    ] as const;
    }


export const getGetEmailPreferencesQueryOptions = <TData = Awaited<ReturnType<typeof getEmailPreferences>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEmailPreferences>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEmailPreferencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEmailPreferences>>> = ({ signal }) => getEmailPreferences({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEmailPreferences>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetEmailPreferencesQueryResult = NonNullable<Awaited<ReturnType<typeof getEmailPreferences>>>
export type GetEmailPreferencesQueryError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


export function useGetEmailPreferences<TData = Awaited<ReturnType<typeof getEmailPreferences>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEmailPreferences>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEmailPreferences>>,
          TError,
          Awaited<ReturnType<typeof getEmailPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetEmailPreferences<TData = Awaited<ReturnType<typeof getEmailPreferences>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEmailPreferences>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEmailPreferences>>,
          TError,
          Awaited<ReturnType<typeof getEmailPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetEmailPreferences<TData = Awaited<ReturnType<typeof getEmailPreferences>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEmailPreferences>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The optional emails the caller receives
 */

export function useGetEmailPreferences<TData = Awaited<ReturnType<typeof getEmailPreferences>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEmailPreferences>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetEmailPreferencesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getSetEmailPreferencesUrl = () => {




  return `/account/email-preferences`
}

/**
 * Both preferences are required (422 `invalid_email_preferences`).
 * @summary Turn the optional emails on or off
 */
export const setEmailPreferences = async (emailPreferences: EmailPreferences, options?: Parameters<typeof apiFetch>[1]): Promise<EmailPreferences> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<EmailPreferences>(getSetEmailPreferencesUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailPreferences)
  }
);}





export const getSetEmailPreferencesMutationKey = () => ['setEmailPreferences'] as const;

export const getSetEmailPreferencesMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setEmailPreferences>>, TError,SetEmailPreferencesMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setEmailPreferences>>, TError,SetEmailPreferencesMutationVariables, TContext> => {

const mutationKey = getSetEmailPreferencesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setEmailPreferences>>, SetEmailPreferencesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  setEmailPreferences(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetEmailPreferencesMutationResult = NonNullable<Awaited<ReturnType<typeof setEmailPreferences>>>
    export type SetEmailPreferencesMutationBody = EmailPreferences
    export type SetEmailPreferencesMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type SetEmailPreferencesMutationVariables = {data: EmailPreferences}

    /**
 * @summary Turn the optional emails on or off
 */
export const useSetEmailPreferences = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setEmailPreferences>>, TError,SetEmailPreferencesMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setEmailPreferences>>,
        TError,
        SetEmailPreferencesMutationVariables,
        TContext
      > => {
      return useMutation(getSetEmailPreferencesMutationOptions(options), queryClient);
    }
    export const getAdminListPermissionsUrl = () => {




  return `/admin/permissions`
}

/**
 * @summary Every permission, grouped by module
 */
export const adminListPermissions = async ( options?: Parameters<typeof apiFetch>[1]): Promise<AdminListPermissions200> => {

  return apiFetch<AdminListPermissions200>(getAdminListPermissionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListPermissionsQueryKey = () => {
    return [
    `/admin/permissions`
    ] as const;
    }


export const getAdminListPermissionsQueryOptions = <TData = Awaited<ReturnType<typeof adminListPermissions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPermissions>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListPermissionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListPermissions>>> = ({ signal }) => adminListPermissions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListPermissions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListPermissionsQueryResult = NonNullable<Awaited<ReturnType<typeof adminListPermissions>>>
export type AdminListPermissionsQueryError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListPermissions<TData = Awaited<ReturnType<typeof adminListPermissions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPermissions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPermissions>>,
          TError,
          Awaited<ReturnType<typeof adminListPermissions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPermissions<TData = Awaited<ReturnType<typeof adminListPermissions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPermissions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPermissions>>,
          TError,
          Awaited<ReturnType<typeof adminListPermissions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPermissions<TData = Awaited<ReturnType<typeof adminListPermissions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPermissions>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every permission, grouped by module
 */

export function useAdminListPermissions<TData = Awaited<ReturnType<typeof adminListPermissions>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPermissions>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListPermissionsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListRolesUrl = () => {




  return `/admin/roles`
}

/**
 * @summary Every role
 */
export const adminListRoles = async ( options?: Parameters<typeof apiFetch>[1]): Promise<AdminListRoles200> => {

  return apiFetch<AdminListRoles200>(getAdminListRolesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListRolesQueryKey = () => {
    return [
    `/admin/roles`
    ] as const;
    }


export const getAdminListRolesQueryOptions = <TData = Awaited<ReturnType<typeof adminListRoles>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRoles>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListRolesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListRoles>>> = ({ signal }) => adminListRoles({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListRoles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListRolesQueryResult = NonNullable<Awaited<ReturnType<typeof adminListRoles>>>
export type AdminListRolesQueryError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListRoles<TData = Awaited<ReturnType<typeof adminListRoles>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRoles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListRoles>>,
          TError,
          Awaited<ReturnType<typeof adminListRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListRoles<TData = Awaited<ReturnType<typeof adminListRoles>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRoles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListRoles>>,
          TError,
          Awaited<ReturnType<typeof adminListRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListRoles<TData = Awaited<ReturnType<typeof adminListRoles>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRoles>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every role
 */

export function useAdminListRoles<TData = Awaited<ReturnType<typeof adminListRoles>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRoles>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListRolesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminCreateRoleUrl = () => {




  return `/admin/roles`
}

/**
 * @summary Create a role
 */
export const adminCreateRole = async (roleInputUnknownRequiredPickRoleInputUnknownSlug: RoleInput & unknown & Required<Pick<RoleInput & unknown, 'slug'>>, options?: Parameters<typeof apiFetch>[1]): Promise<Role> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Role>(getAdminCreateRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(roleInputUnknownRequiredPickRoleInputUnknownSlug)
  }
);}





export const getAdminCreateRoleMutationKey = () => ['adminCreateRole'] as const;

export const getAdminCreateRoleMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminCreateRole>>, TError,AdminCreateRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminCreateRole>>, TError,AdminCreateRoleMutationVariables, TContext> => {

const mutationKey = getAdminCreateRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminCreateRole>>, AdminCreateRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  adminCreateRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminCreateRoleMutationResult = NonNullable<Awaited<ReturnType<typeof adminCreateRole>>>
    export type AdminCreateRoleMutationBody = RoleInput & unknown & Required<Pick<RoleInput & unknown, 'slug'>>
    export type AdminCreateRoleMutationError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminCreateRoleMutationVariables = {data: RoleInput & unknown & Required<Pick<RoleInput & unknown, 'slug'>>}

    /**
 * @summary Create a role
 */
export const useAdminCreateRole = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminCreateRole>>, TError,AdminCreateRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminCreateRole>>,
        TError,
        AdminCreateRoleMutationVariables,
        TContext
      > => {
      return useMutation(getAdminCreateRoleMutationOptions(options), queryClient);
    }
    export const getAdminGetRoleUrl = (id: string,) => {




  return `/admin/roles/${id}`
}

/**
 * @summary One role
 */
export const adminGetRole = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Role> => {

  return apiFetch<Role>(getAdminGetRoleUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetRoleQueryKey = (id: string,) => {
    return [
    `/admin/roles/${id}`
    ] as const;
    }


export const getAdminGetRoleQueryOptions = <TData = Awaited<ReturnType<typeof adminGetRole>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRole>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetRoleQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetRole>>> = ({ signal }) => adminGetRole(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetRole>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetRoleQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetRole>>>
export type AdminGetRoleQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetRole<TData = Awaited<ReturnType<typeof adminGetRole>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRole>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetRole>>,
          TError,
          Awaited<ReturnType<typeof adminGetRole>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetRole<TData = Awaited<ReturnType<typeof adminGetRole>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRole>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetRole>>,
          TError,
          Awaited<ReturnType<typeof adminGetRole>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetRole<TData = Awaited<ReturnType<typeof adminGetRole>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRole>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One role
 */

export function useAdminGetRole<TData = Awaited<ReturnType<typeof adminGetRole>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRole>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetRoleQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminUpdateRoleUrl = (id: string,) => {




  return `/admin/roles/${id}`
}

/**
 * The whole role is replaced by the body. A system role cannot change (409 `role_is_system`).
 * @summary Change a role
 */
export const adminUpdateRole = async (id: string,
    roleInput: RoleInput, options?: Parameters<typeof apiFetch>[1]): Promise<Role> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Role>(getAdminUpdateRoleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(roleInput)
  }
);}





export const getAdminUpdateRoleMutationKey = () => ['adminUpdateRole'] as const;

export const getAdminUpdateRoleMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminUpdateRole>>, TError,AdminUpdateRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminUpdateRole>>, TError,AdminUpdateRoleMutationVariables, TContext> => {

const mutationKey = getAdminUpdateRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminUpdateRole>>, AdminUpdateRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminUpdateRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminUpdateRoleMutationResult = NonNullable<Awaited<ReturnType<typeof adminUpdateRole>>>
    export type AdminUpdateRoleMutationBody = RoleInput
    export type AdminUpdateRoleMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminUpdateRoleMutationVariables = {id: string;data: RoleInput}

    /**
 * @summary Change a role
 */
export const useAdminUpdateRole = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminUpdateRole>>, TError,AdminUpdateRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminUpdateRole>>,
        TError,
        AdminUpdateRoleMutationVariables,
        TContext
      > => {
      return useMutation(getAdminUpdateRoleMutationOptions(options), queryClient);
    }
    export const getAdminDeleteRoleUrl = (id: string,
    params?: AdminDeleteRoleParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/roles/${id}?${stringifiedParams}` : `/admin/roles/${id}`
}

/**
 * A role still held needs `reassignTo` (409 `role_in_use`).
 * @summary Delete a role
 */
export const adminDeleteRole = async (id: string,
    params?: AdminDeleteRoleParams, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getAdminDeleteRoleUrl(id,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getAdminDeleteRoleMutationKey = () => ['adminDeleteRole'] as const;

export const getAdminDeleteRoleMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminDeleteRole>>, TError,AdminDeleteRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminDeleteRole>>, TError,AdminDeleteRoleMutationVariables, TContext> => {

const mutationKey = getAdminDeleteRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminDeleteRole>>, AdminDeleteRoleMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  adminDeleteRole(id,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminDeleteRoleMutationResult = NonNullable<Awaited<ReturnType<typeof adminDeleteRole>>>

    export type AdminDeleteRoleMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminDeleteRoleMutationVariables = {id: string;params?: AdminDeleteRoleParams}

    /**
 * @summary Delete a role
 */
export const useAdminDeleteRole = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminDeleteRole>>, TError,AdminDeleteRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminDeleteRole>>,
        TError,
        AdminDeleteRoleMutationVariables,
        TContext
      > => {
      return useMutation(getAdminDeleteRoleMutationOptions(options), queryClient);
    }
    export const getAdminListUsersUrl = (params?: AdminListUsersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/users?${stringifiedParams}` : `/admin/users`
}

/**
 * @summary Every account, newest first
 */
export const adminListUsers = async (params?: AdminListUsersParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminUserPage> => {

  return apiFetch<AdminUserPage>(getAdminListUsersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListUsersInfiniteQueryKey = (params?: AdminListUsersParams,) => {
    return [
    'infinite', `/admin/users`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListUsersQueryKey = (params?: AdminListUsersParams,) => {
    return [
    `/admin/users`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListUsersInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListUsers>>, AdminListUsersParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListUsersParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData, QueryKey, AdminListUsersParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListUsersInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListUsers>>, QueryKey, AdminListUsersParams['cursor']> = ({ signal, pageParam }) => adminListUsers({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData, QueryKey, AdminListUsersParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListUsersInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListUsers>>>
export type AdminListUsersInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListUsersInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListUsers>>, AdminListUsersParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListUsersParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData, QueryKey, AdminListUsersParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListUsers>>,
          TError,
          Awaited<ReturnType<typeof adminListUsers>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListUsersInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListUsers>>, AdminListUsersParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListUsersParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData, QueryKey, AdminListUsersParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListUsers>>,
          TError,
          Awaited<ReturnType<typeof adminListUsers>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListUsersInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListUsers>>, AdminListUsersParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListUsersParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData, QueryKey, AdminListUsersParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every account, newest first
 */

export function useAdminListUsersInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListUsers>>, AdminListUsersParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListUsersParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData, QueryKey, AdminListUsersParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListUsersInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListUsersQueryOptions = <TData = Awaited<ReturnType<typeof adminListUsers>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListUsersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListUsers>>> = ({ signal }) => adminListUsers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListUsersQueryResult = NonNullable<Awaited<ReturnType<typeof adminListUsers>>>
export type AdminListUsersQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListUsers<TData = Awaited<ReturnType<typeof adminListUsers>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListUsersParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListUsers>>,
          TError,
          Awaited<ReturnType<typeof adminListUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListUsers<TData = Awaited<ReturnType<typeof adminListUsers>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListUsers>>,
          TError,
          Awaited<ReturnType<typeof adminListUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListUsers<TData = Awaited<ReturnType<typeof adminListUsers>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every account, newest first
 */

export function useAdminListUsers<TData = Awaited<ReturnType<typeof adminListUsers>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListUsers>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListUsersQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminInviteStaffUrl = () => {




  return `/admin/users/invite`
}

/**
 * Creates the account with a staff role and mails a 7-day invitation.
 * @summary Invite a staff member
 */
export const adminInviteStaff = async (adminInviteStaffBody: AdminInviteStaffBody, options?: Parameters<typeof apiFetch>[1]): Promise<AdminInviteStaff201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<AdminInviteStaff201>(getAdminInviteStaffUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminInviteStaffBody)
  }
);}





export const getAdminInviteStaffMutationKey = () => ['adminInviteStaff'] as const;

export const getAdminInviteStaffMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminInviteStaff>>, TError,AdminInviteStaffMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminInviteStaff>>, TError,AdminInviteStaffMutationVariables, TContext> => {

const mutationKey = getAdminInviteStaffMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminInviteStaff>>, AdminInviteStaffMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  adminInviteStaff(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminInviteStaffMutationResult = NonNullable<Awaited<ReturnType<typeof adminInviteStaff>>>
    export type AdminInviteStaffMutationBody = AdminInviteStaffBody
    export type AdminInviteStaffMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminInviteStaffMutationVariables = {data: AdminInviteStaffBody}

    /**
 * @summary Invite a staff member
 */
export const useAdminInviteStaff = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminInviteStaff>>, TError,AdminInviteStaffMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminInviteStaff>>,
        TError,
        AdminInviteStaffMutationVariables,
        TContext
      > => {
      return useMutation(getAdminInviteStaffMutationOptions(options), queryClient);
    }
    export const getAdminGetUserUrl = (id: string,) => {




  return `/admin/users/${id}`
}

/**
 * @summary One account with its role and effective permissions
 */
export const adminGetUser = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<AdminUserDetail> => {

  return apiFetch<AdminUserDetail>(getAdminGetUserUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetUserQueryKey = (id: string,) => {
    return [
    `/admin/users/${id}`
    ] as const;
    }


export const getAdminGetUserQueryOptions = <TData = Awaited<ReturnType<typeof adminGetUser>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetUser>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetUserQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetUser>>> = ({ signal }) => adminGetUser(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetUser>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetUserQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetUser>>>
export type AdminGetUserQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetUser<TData = Awaited<ReturnType<typeof adminGetUser>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetUser>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetUser>>,
          TError,
          Awaited<ReturnType<typeof adminGetUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetUser<TData = Awaited<ReturnType<typeof adminGetUser>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetUser>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetUser>>,
          TError,
          Awaited<ReturnType<typeof adminGetUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetUser<TData = Awaited<ReturnType<typeof adminGetUser>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetUser>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One account with its role and effective permissions
 */

export function useAdminGetUser<TData = Awaited<ReturnType<typeof adminGetUser>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetUser>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetUserQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminDeleteUserUrl = (id: string,) => {




  return `/admin/users/${id}`
}

/**
 * @summary Delete and anonymise an account
 */
export const adminDeleteUser = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getAdminDeleteUserUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getAdminDeleteUserMutationKey = () => ['adminDeleteUser'] as const;

export const getAdminDeleteUserMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminDeleteUser>>, TError,AdminDeleteUserMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminDeleteUser>>, TError,AdminDeleteUserMutationVariables, TContext> => {

const mutationKey = getAdminDeleteUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminDeleteUser>>, AdminDeleteUserMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  adminDeleteUser(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminDeleteUserMutationResult = NonNullable<Awaited<ReturnType<typeof adminDeleteUser>>>

    export type AdminDeleteUserMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminDeleteUserMutationVariables = {id: string}

    /**
 * @summary Delete and anonymise an account
 */
export const useAdminDeleteUser = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminDeleteUser>>, TError,AdminDeleteUserMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminDeleteUser>>,
        TError,
        AdminDeleteUserMutationVariables,
        TContext
      > => {
      return useMutation(getAdminDeleteUserMutationOptions(options), queryClient);
    }
    export const getAdminSetUserRoleUrl = (id: string,) => {




  return `/admin/users/${id}/role`
}

/**
 * @summary Give an account another role
 */
export const adminSetUserRole = async (id: string,
    adminSetUserRoleBody: AdminSetUserRoleBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getAdminSetUserRoleUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSetUserRoleBody)
  }
);}





export const getAdminSetUserRoleMutationKey = () => ['adminSetUserRole'] as const;

export const getAdminSetUserRoleMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminSetUserRole>>, TError,AdminSetUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminSetUserRole>>, TError,AdminSetUserRoleMutationVariables, TContext> => {

const mutationKey = getAdminSetUserRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminSetUserRole>>, AdminSetUserRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminSetUserRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminSetUserRoleMutationResult = NonNullable<Awaited<ReturnType<typeof adminSetUserRole>>>
    export type AdminSetUserRoleMutationBody = AdminSetUserRoleBody
    export type AdminSetUserRoleMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminSetUserRoleMutationVariables = {id: string;data: AdminSetUserRoleBody}

    /**
 * @summary Give an account another role
 */
export const useAdminSetUserRole = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminSetUserRole>>, TError,AdminSetUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminSetUserRole>>,
        TError,
        AdminSetUserRoleMutationVariables,
        TContext
      > => {
      return useMutation(getAdminSetUserRoleMutationOptions(options), queryClient);
    }
    export const getAdminSetUserBannedUrl = (id: string,) => {




  return `/admin/users/${id}/banned`
}

/**
 * A ban ends every session and revokes nothing else.
 * @summary Ban or unban an account
 */
export const adminSetUserBanned = async (id: string,
    adminSetUserBannedBody: AdminSetUserBannedBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getAdminSetUserBannedUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSetUserBannedBody)
  }
);}





export const getAdminSetUserBannedMutationKey = () => ['adminSetUserBanned'] as const;

export const getAdminSetUserBannedMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminSetUserBanned>>, TError,AdminSetUserBannedMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminSetUserBanned>>, TError,AdminSetUserBannedMutationVariables, TContext> => {

const mutationKey = getAdminSetUserBannedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminSetUserBanned>>, AdminSetUserBannedMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminSetUserBanned(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminSetUserBannedMutationResult = NonNullable<Awaited<ReturnType<typeof adminSetUserBanned>>>
    export type AdminSetUserBannedMutationBody = AdminSetUserBannedBody
    export type AdminSetUserBannedMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminSetUserBannedMutationVariables = {id: string;data: AdminSetUserBannedBody}

    /**
 * @summary Ban or unban an account
 */
export const useAdminSetUserBanned = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminSetUserBanned>>, TError,AdminSetUserBannedMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminSetUserBanned>>,
        TError,
        AdminSetUserBannedMutationVariables,
        TContext
      > => {
      return useMutation(getAdminSetUserBannedMutationOptions(options), queryClient);
    }
    export const getAdminSetUserPermissionUrl = (id: string,
    permission: string,) => {




  return `/admin/users/${id}/permissions/${permission}`
}

/**
 * @summary Grant or revoke one permission for one account
 */
export const adminSetUserPermission = async (id: string,
    permission: string,
    adminSetUserPermissionBody: AdminSetUserPermissionBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getAdminSetUserPermissionUrl(id,permission),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSetUserPermissionBody)
  }
);}





export const getAdminSetUserPermissionMutationKey = () => ['adminSetUserPermission'] as const;

export const getAdminSetUserPermissionMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminSetUserPermission>>, TError,AdminSetUserPermissionMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminSetUserPermission>>, TError,AdminSetUserPermissionMutationVariables, TContext> => {

const mutationKey = getAdminSetUserPermissionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminSetUserPermission>>, AdminSetUserPermissionMutationVariables> = (props) => {
          const {id,permission,data} = props ?? {};

          return  adminSetUserPermission(id,permission,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminSetUserPermissionMutationResult = NonNullable<Awaited<ReturnType<typeof adminSetUserPermission>>>
    export type AdminSetUserPermissionMutationBody = AdminSetUserPermissionBody
    export type AdminSetUserPermissionMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminSetUserPermissionMutationVariables = {id: string;permission: string;data: AdminSetUserPermissionBody}

    /**
 * @summary Grant or revoke one permission for one account
 */
export const useAdminSetUserPermission = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminSetUserPermission>>, TError,AdminSetUserPermissionMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminSetUserPermission>>,
        TError,
        AdminSetUserPermissionMutationVariables,
        TContext
      > => {
      return useMutation(getAdminSetUserPermissionMutationOptions(options), queryClient);
    }
    export const getAdminClearUserPermissionUrl = (id: string,
    permission: string,) => {




  return `/admin/users/${id}/permissions/${permission}`
}

/**
 * The body's `reason` is required.
 * @summary Remove an account's override of one permission
 */
export const adminClearUserPermission = async (id: string,
    permission: string,
    adminClearUserPermissionBody: AdminClearUserPermissionBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getAdminClearUserPermissionUrl(id,permission),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminClearUserPermissionBody)
  }
);}





export const getAdminClearUserPermissionMutationKey = () => ['adminClearUserPermission'] as const;

export const getAdminClearUserPermissionMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminClearUserPermission>>, TError,AdminClearUserPermissionMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminClearUserPermission>>, TError,AdminClearUserPermissionMutationVariables, TContext> => {

const mutationKey = getAdminClearUserPermissionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminClearUserPermission>>, AdminClearUserPermissionMutationVariables> = (props) => {
          const {id,permission,data} = props ?? {};

          return  adminClearUserPermission(id,permission,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminClearUserPermissionMutationResult = NonNullable<Awaited<ReturnType<typeof adminClearUserPermission>>>
    export type AdminClearUserPermissionMutationBody = AdminClearUserPermissionBody
    export type AdminClearUserPermissionMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminClearUserPermissionMutationVariables = {id: string;permission: string;data: AdminClearUserPermissionBody}

    /**
 * @summary Remove an account's override of one permission
 */
export const useAdminClearUserPermission = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminClearUserPermission>>, TError,AdminClearUserPermissionMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminClearUserPermission>>,
        TError,
        AdminClearUserPermissionMutationVariables,
        TContext
      > => {
      return useMutation(getAdminClearUserPermissionMutationOptions(options), queryClient);
    }
